import React from 'react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Heart, Clock, MessageCircle, MapPin, Music, Zap } from 'lucide-react';
import { UserProfile } from '@/types';
import TrackPreviewCompact from '../TrackPreviewCompact';

interface MatchesViewProps {
  matchedProfiles: (UserProfile & { 
    matchScore?: any;
    matchedDate?: Date;
  })[];
  onSwitchToDiscovery: () => void;
}

const MatchesView: React.FC<MatchesViewProps> = ({ 
  matchedProfiles,
  onSwitchToDiscovery
}) => {
  if (matchedProfiles.length === 0) {
    return (
      <div className="text-center py-12">
        <Heart className="h-16 w-16 mx-auto mb-4 opacity-20" />
        <h3 className="text-lg font-medium mb-2">No matches yet</h3>
        <p className="text-muted-foreground mb-6">
          Start discovering musicians to find your perfect collaborators
        </p>
        <Button onClick={onSwitchToDiscovery}>
          Discover Musicians
        </Button>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
      {matchedProfiles.map(profile => (
        <MatchCard key={profile.userId} profile={profile} />
      ))}
    </div>
  );
};

interface MatchCardProps {
  profile: UserProfile & { 
    matchScore?: any;
    matchedDate?: Date;
  };
}

const MatchCard: React.FC<MatchCardProps> = ({ profile }) => {
  const getCompatibilityColor = (score: number) => {
    if (score >= 90) return 'text-green-500';
    if (score >= 80) return 'text-emerald-500';
    if (score >= 70) return 'text-yellow-500';
    return 'text-orange-500';
  };

  return (
    <Card className="overflow-hidden hover:shadow-md transition-shadow">
      <div className="aspect-square">
        <img 
          src={profile.avatarUrl} 
          alt={profile.name} 
          className="w-full h-full object-cover"
        />
      </div>
      <CardHeader className="pb-2">
        <div className="flex justify-between items-start">
          <CardTitle className="text-lg">{profile.name}</CardTitle>
          <Badge className={getCompatibilityColor(profile.matchScore?.score || 0)}>
            {profile.matchScore?.score}%
          </Badge>
        </div>
        <CardDescription className="flex flex-col gap-1">
          <span>{profile.role}</span>
          <span className="flex items-center gap-1">
            <MapPin className="h-3 w-3" />
            {profile.location}
          </span>
        </CardDescription>
      </CardHeader>
      <CardContent className="pb-2">
        <div className="flex flex-wrap gap-1 mb-2">
          {profile.genres.slice(0, 3).map(genre => (
            <Badge key={genre} variant="outline" className="text-xs">
              {genre}
            </Badge>
          ))}
        </div>
        
        {profile.spotifyProfile?.topTracks && profile.spotifyProfile.topTracks.length > 0 && (
          <div className="mt-3">
            <h4 className="text-xs font-medium mb-2 flex items-center gap-1">
              <Music className="h-3 w-3" />
              <span>Popular Track</span>
            </h4>
            <TrackPreviewCompact 
              track={profile.spotifyProfile.topTracks[0]} 
            />
          </div>
        )}
        
        {profile.matchScore?.compatibilityReasons && profile.matchScore.compatibilityReasons.length > 0 && (
          <div className="mt-3">
            <h4 className="text-xs font-medium mb-1 flex items-center gap-1">
              <Zap className="h-3 w-3 text-yellow-500" />
              <span>Why you match</span>
            </h4>
            <p className="text-xs text-muted-foreground line-clamp-2">
              {profile.matchScore.compatibilityReasons[0]}
            </p>
          </div>
        )}
      </CardContent>
      <CardFooter className="flex justify-between bg-muted/30 pt-3">
        <div className="text-xs text-muted-foreground flex items-center gap-1">
          <Clock className="h-3 w-3" />
          <span>Matched {profile.matchedDate?.toLocaleDateString()}</span>
        </div>
        <Button variant="ghost" size="sm" className="gap-1">
          <MessageCircle className="h-4 w-4" />
          <span>Message</span>
        </Button>
      </CardFooter>
    </Card>
  );
};

export default MatchesView;
